<article class="message-balloon" [class.message-balloon--user]="isUser()" [class.message-balloon--assistant]="!isUser()"
  [class.message-balloon--last-assistant]="isLastAssistant()">
  <div class="message-balloon__body">
    @if (!isUser() && latestNote(); as note) {
    <button type="button" class="message-balloon__note" [class.message-balloon__note--active]="noteActivityActive()"
      (click)="toggleNotesClick($event)">
      <span class="message-balloon__note-icon" aria-hidden="true">
        <app-icon [icon]="noteIcon(note.type)" color="currentColor" [width]="14" [height]="14" />
      </span>
      <span class="message-balloon__note-label" [attr.data-text]="noteStatus(note)">{{ noteStatus(note) }}</span>
    </button>
    }

    @if (!isUser() && mcpApproval(); as approval) {
    <section class="message-balloon__mcp-approval" aria-live="polite"
      [class.message-balloon__mcp-approval--submitting]="approval.status === 'submitting'">
      <div class="message-balloon__mcp-approval-heading">
        <span class="message-balloon__mcp-approval-icon" aria-hidden="true">
          <app-icon icon="fa-solid fa-shield-halved" color="currentColor" [width]="16" [height]="16" />
        </span>
        <div>
          <strong>{{ 'chat.message.mcpApproval.title' | translate: { app: approval.appName } }}</strong>
          <p>{{ 'chat.message.mcpApproval.description' | translate: { tool: mcpToolLabel(approval) } }}</p>
        </div>
      </div>

      @if (mcpArguments(approval); as arguments) {
      <details class="message-balloon__mcp-approval-details">
        <summary>{{ 'chat.message.mcpApproval.details' | translate }}</summary>
        <pre>{{ arguments }}</pre>
      </details>
      }

      @if (approval.error) {
      <p class="message-balloon__mcp-approval-error">{{ approval.error }}</p>
      }

      <div class="message-balloon__mcp-approval-actions">
        <button type="button" class="message-balloon__mcp-approval-button"
          [disabled]="approval.status === 'submitting'" (click)="decideMcpApproval($event, false)">
          {{ 'chat.message.mcpApproval.deny' | translate }}
        </button>
        <button type="button" class="message-balloon__mcp-approval-button message-balloon__mcp-approval-button--allow"
          [disabled]="approval.status === 'submitting'" (click)="decideMcpApproval($event, true)">
          @if (approval.status === 'submitting') {
          <span class="message-balloon__mcp-approval-spinner" aria-hidden="true"></span>
          {{ 'chat.message.mcpApproval.processing' | translate }}
          } @else {
          <app-icon icon="fa-solid fa-check" color="currentColor" [width]="12" [height]="12" />
          {{ 'chat.message.mcpApproval.allow' | translate }}
          }
        </button>
      </div>
    </section>
    }

    @if (messageText()) {
    @if (isImageGenerationVisible()) {
    <output class="message-balloon__image-loader"
      [class.message-balloon__image-loader--cancelled]="isImageGenerationCancelled()" aria-live="polite">
      <div class="message-balloon__image-loader-header">
        <span class="message-balloon__image-loader-title">
          <app-icon icon="icon-stars-solid.svg" color="currentColor" [width]="18" [height]="18" />
          {{ 'chat.message.imageGeneration.title' | translate }}
        </span>
        <span class="message-balloon__image-loader-status">
          <span aria-hidden="true"></span>
          @if (isImageGenerationCancelled()) {
          {{ 'chat.message.imageGeneration.stoppedStatus' | translate }}
          } @else {
          {{ 'chat.message.imageGeneration.status' | translate }}
          }
        </span>
      </div>
      <div class="message-balloon__image-loader-canvas">
        @if (isImageGenerationCancelled()) {
        <strong>{{ 'chat.message.imageGeneration.cancelledTitle' | translate }}</strong>
        } @else {
        <div class="message-balloon__image-loader-spinner" aria-hidden="true">
          <span></span>
        </div>
        <strong>{{ imageGenerationNote() }}</strong>
        <p>{{ 'chat.message.imageGeneration.description' | translate }}</p>
        }
      </div>
    </output>
    } @else if (isUser()) {
    @if (isEditing()) {
    <div class="message-balloon__editor">
      <textarea #editTextarea #autosize="cdkTextareaAutosize" cdkTextareaAutosize [cdkAutosizeMinRows]="4"
        [cdkAutosizeMaxRows]="7" class="message-balloon__textarea" [ngModel]="editedText()"
        (ngModelChange)="editedText.set($event)" (keydown)="onEditKeydown($event)" (click)="$event.stopPropagation()"
        [attr.aria-label]="'chat.message.editAriaLabel' | translate" rows="4"></textarea>

      <div class="message-balloon__editor-actions">
        <button type="button" class="message-balloon__editor-button" (click)="cancelEditingClick($event)">
          {{ 'common.actions.cancel' | translate }}
        </button>
        <button type="button" class="message-balloon__editor-button message-balloon__editor-button--primary"
          [disabled]="!editedText().trim() || !hasEditChanges()" (click)="saveEditingClick($event)">
          {{ 'chat.actions.send' | translate }}
        </button>
      </div>
    </div>
    } @else {
    <button type="button" class="message-balloon__edit-trigger" (click)="startEditingClick($event)">{{ messageText()
      }}</button>
    }
    } @else {
    <div #assistantMessageContent class="message-balloon__markdown">
      <markdown [data]="messageText()" [mermaidOptions]="mermaidOptions()" ngPreserveWhitespaces emoji katex mermaid>
      </markdown>
    </div>
    }
    } @else {
    <span class="message-balloon__typing" [attr.aria-label]="'chat.message.generatingAnswer' | translate"></span>
    }
  </div>

  @if (!isImageGenerationVisible()) {
  @if (isUser() && mediaOpen() && media().length > 0) {
  <div class="message-balloon__files" [attr.aria-label]="'chat.message.attachedFiles' | translate">
    @for (attachment of media(); track attachment.mediaId) {
    <button type="button" class="message-balloon__file" [disabled]="isMediaDownloading(attachment.mediaId)"
      [attr.aria-label]="(isImageMedia(attachment) ? 'chat.message.openAttachedFile' : 'chat.message.downloadAttachedFile') | translate: { name: attachment.fileName }"
      (click)="mediaClick($event, attachment)">
      <app-icon-file class="message-balloon__file-icon" [fileName]="attachment.fileName" [size]="30" />
      <div class="message-balloon__file-text">
        <strong>{{ attachment.fileName }}</strong>
        <span>{{ getMediaKind(attachment) }}</span>
      </div>
    </button>
    }
  </div>
  }

  @if (!isEditing()) {
  <div class="message-balloon__actions" [attr.aria-label]="'chat.message.actionsAriaLabel' | translate">
    @if (isUser()) {
    @if (media().length > 0) {
    <button type="button" class="message-balloon__files-trigger"
      [class.message-balloon__files-trigger--active]="mediaOpen()"
      [attr.aria-label]="'chat.message.toggleAttachedFiles' | translate" (click)="toggleMediaClick($event)">
      <span class="message-balloon__source-icons" aria-hidden="true">
        @for (attachment of visibleMedia(); track attachment.mediaId; let index = $index) {
        <span class="message-balloon__source-icon" [style.z-index]="visibleMedia().length - index">
          <app-icon-file [fileName]="attachment.fileName" [size]="16" />
        </span>
        }
      </span>
      <span>{{ 'chat.message.files' | translate }} ({{ media().length }})</span>
    </button>
    }
    <button type="button" [attr.aria-label]="'chat.message.editAriaLabel' | translate"
      (click)="startEditingClick($event)">
      <app-icon icon="icon-pencil-regular.svg" color="currentColor" [width]="15" [height]="15" />
    </button>
    <button type="button" [attr.aria-label]="'chat.message.copyMessage' | translate" [disabled]="copiedToClipboard()"
      (click)="copyMessage()">
      <app-icon [icon]="copiedToClipboard() ? 'icon-check-solid.svg' : 'icon-duplicate-regular.svg'"
        color="currentColor" [width]="15" [height]="15" />
    </button>
    <app-button-confirm-callout [label]="'chat.message.deleteMessage' | translate" icon="far fa-trash-can" [iconHeight]="11"
      calloutColor="danger" (confirmed)="deleteMessageClick()"></app-button-confirm-callout>
    } @else {
    <button type="button" [attr.aria-label]="'chat.message.regenerateAnswer' | translate"
      (click)="regenerateMessageClick($event)">
      <app-icon icon="icon-refresh-regular.svg" color="currentColor" [width]="15" [height]="15" />
    </button>
    <button type="button" [attr.aria-label]="'chat.message.copyAnswer' | translate" [disabled]="copiedToClipboard()"
      (click)="copyMessage()">
      <app-icon [icon]="copiedToClipboard() ? 'icon-check-solid.svg' : 'icon-duplicate-regular.svg'"
        color="currentColor" [width]="15" [height]="15" />
    </button>
    <app-button-dropdown class="message-balloon__dropdown" buttonType="icon" icon="icon-dots-horizontal.svg"
      [iconColor]="'currentColor'" [label]="'chat.message.moreActions' | translate" [items]="assistantMessageActions()"
      [iconSize]="12" (itemSelected)="selectAssistantMessageAction($event)" />
    @if (sources().length > 0) {
    <button type="button" class="message-balloon__sources-trigger" aria-label="Sources"
      (click)="toggleSourcesClick($event)">
      <span class="message-balloon__source-icons" aria-hidden="true">
        @for (source of visibleSources(); track (source.url || source.mediaUrl || source.fileName); let index = $index)
        {
        <span class="message-balloon__source-icon" [style.z-index]="visibleSources().length - index">
          @if (source.iconUrl) {
          <img [src]="source.iconUrl" alt="" />
          } @else {
          <app-icon [icon]="sourceIcon(source)" color="currentColor" [width]="12" [height]="12" />
          }
        </span>
        }
      </span>
      <span>Sources</span>
    </button>
    }
    }
  </div>
  }
  }
</article>
